import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "calendar-page" }
const _hoisted_2 = {
  key: 0,
  class: "calendar-page__notice",
  role: "status"
}
const _hoisted_3 = {
  class: "calendar-page__nav",
  "aria-label": "Режим модуля"
}
const _hoisted_4 = ["aria-pressed"]
const _hoisted_5 = ["aria-pressed"]
const _hoisted_6 = {
  key: 0,
  class: "calendar-probe"
}
const _hoisted_7 = { class: "calendar-probe__actions" }
const _hoisted_8 = ["disabled"]
const _hoisted_9 = ["disabled"]
const _hoisted_10 = {
  role: "status",
  "aria-live": "polite"
}
const _hoisted_11 = { key: 0 }
const _hoisted_12 = {
  key: 1,
  class: "calendar-page__notice",
  role: "status"
}

import {
  computed, onBeforeUnmount, onMounted, ref, watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import CalendarWidget from '@/components/CalendarWidget.vue';
// The existing auth store imports the router; it is read after setup starts.
// eslint-disable-next-line import/no-cycle
import { useAuthStore } from '@/store/auth/authModule';
import { useCalendarStore } from '@/store/calendar/calendarModule';
import { parseIdPatient } from '@/utils/parentAuth';
import { isEmbedded, onParentMessage, postToParent } from '@/utils/patientBridge';


export default /*@__PURE__*/_defineComponent({
  __name: 'CalendarView',
  setup(__props) {

const route = useRoute();
const router = useRouter();
const calendarStore = useCalendarStore();
const { isAuth } = storeToRefs(useAuthStore());
const { idPatient } = storeToRefs(calendarStore);
const contextGeneration = ref(0);
const busy = ref(false);
const probeMessage = ref('');
const rows = ref<Array<{ id: unknown; id_patient: unknown }>>([]);
const diagnostics = computed(() => route.query.mode === 'diagnostics');
// Филиал: из URL, а если его там нет — тот, что прислал родитель по запросу
const parentBranch = ref(0);
const idBranch = computed(() => parseIdPatient(route.query.id_branch) || parentBranch.value);
const branchNotice = computed(() => (
  isEmbedded()
    ? 'Выберите филиал в основном приложении — календарь откроется автоматически.'
    : 'Филиал не передан. Откройте календарь из основного приложения.'
));
const calendarContext = computed(() => ({
  id_patient: idPatient.value || null,
  id_staff: parseIdPatient(route.query.id_staff) || null,
  id_branch: idBranch.value || null,
  ...(typeof route.query.date === 'string' ? { date: route.query.date } : {}),
}));

// Remount even if only authorization changed; old responses cannot paint a new session.
watch(() => [route.query.id_patient, route.query.id_staff, route.query.id_branch,
  route.query.date, route.query.con_str], () => {
  contextGeneration.value += 1;
  calendarStore.setPatient(parseIdPatient(route.query.id_patient));
  rows.value = [];
  probeMessage.value = '';
  busy.value = false;
}, { immediate: true });

const setMode = (showDiagnostics: boolean) => {
  router.replace({ query: { ...route.query, mode: showDiagnostics ? 'diagnostics' : undefined } });
};

let stopParentListener: (() => void) | null = null;

onMounted(() => {
  // Смена филиала у родителя приходит сюда же: контекст виджета обновится без перезагрузки
  stopParentListener = onParentMessage((message) => {
    parentBranch.value = parseIdPatient(String(message.id_branch));
  });
  if (!idBranch.value) postToParent('branch:request');
});

onBeforeUnmount(() => stopParentListener?.());

const probe = async (operation: 'send' | 'receive') => {
  if (busy.value) return;
  const generation = contextGeneration.value;
  busy.value = true;
  probeMessage.value = 'Выполняется запрос…';
  try {
    const response = operation === 'send' ? await calendarStore.sendId() : await calendarStore.receiveId();
    if (generation !== contextGeneration.value) return;
    const first = response?.data?.[0];
    if (!response || response.errorType || first?.err) {
      probeMessage.value = String(first?.err_txt || first?.err_text || response?.errorText || 'Нет ответа сервера.');
      return;
    }
    if (operation === 'receive') {
      if (!Array.isArray(response.data)) {
        probeMessage.value = 'Сервер не вернул массив строк.';
        return;
      }
      rows.value = response.data.filter((row) => 'id_patient' in row)
        .map((row) => ({ id: row.id, id_patient: row.id_patient }));
      probeMessage.value = `Получено строк: ${rows.value.length}.`;
    } else probeMessage.value = 'Сервер подтвердил отправку ID. Нажмите «Прочитать ID», чтобы проверить запись.';
  } catch {
    if (generation === contextGeneration.value) probeMessage.value = 'Не удалось выполнить запрос.';
  } finally {
    if (generation === contextGeneration.value) busy.value = false;
  }
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("main", _hoisted_1, [
    (!_unref(isAuth))
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, " Нет данных авторизации от родительского приложения. Откройте календарь из основной страницы с параметром con_str. "))
      : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
          _createElementVNode("nav", _hoisted_3, [
            _createElementVNode("button", {
              type: "button",
              "aria-pressed": !diagnostics.value,
              onClick: _cache[0] || (_cache[0] = ($event: any) => (setMode(false)))
            }, " Календарь ", 8, _hoisted_4),
            _createElementVNode("button", {
              type: "button",
              "aria-pressed": diagnostics.value,
              onClick: _cache[1] || (_cache[1] = ($event: any) => (setMode(true)))
            }, " Проверка связи с БД ", 8, _hoisted_5)
          ]),
          (diagnostics.value)
            ? (_openBlock(), _createElementBlock("section", _hoisted_6, [
                _cache[6] || (_cache[6] = _createElementVNode("h1", null, "Проверка iframe → API → БД", -1)),
                _createElementVNode("p", null, [
                  _cache[4] || (_cache[4] = _createTextVNode("Пациент с основной страницы: ", -1)),
                  _createElementVNode("strong", null, _toDisplayString(_unref(idPatient) || 'не выбран'), 1)
                ]),
                _cache[7] || (_cache[7] = _createElementVNode("p", null, "Отправка добавляет ID в тестовую таблицу. Чтение показывает строки из этой таблицы.", -1)),
                _createElementVNode("div", _hoisted_7, [
                  _createElementVNode("button", {
                    type: "button",
                    disabled: busy.value || !_unref(idPatient),
                    onClick: _cache[2] || (_cache[2] = ($event: any) => (probe('send')))
                  }, " Отправить ID — send_id ", 8, _hoisted_8),
                  _createElementVNode("button", {
                    type: "button",
                    disabled: busy.value,
                    onClick: _cache[3] || (_cache[3] = ($event: any) => (probe('receive')))
                  }, " Прочитать ID — receive_id ", 8, _hoisted_9)
                ]),
                _createElementVNode("p", _hoisted_10, _toDisplayString(probeMessage.value), 1),
                (rows.value.length)
                  ? (_openBlock(), _createElementBlock("table", _hoisted_11, [
                      _cache[5] || (_cache[5] = _createElementVNode("thead", null, [
                        _createElementVNode("tr", null, [
                          _createElementVNode("th", null, "ID строки"),
                          _createElementVNode("th", null, "ID пациента")
                        ])
                      ], -1)),
                      _createElementVNode("tbody", null, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(rows.value, (row, index) => {
                          return (_openBlock(), _createElementBlock("tr", { key: index }, [
                            _createElementVNode("td", null, _toDisplayString(row.id), 1),
                            _createElementVNode("td", null, _toDisplayString(row.id_patient), 1)
                          ]))
                        }), 128))
                      ])
                    ]))
                  : _createCommentVNode("", true)
              ]))
            : (!idBranch.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_12, _toDisplayString(branchNotice.value), 1))
              : (_openBlock(), _createBlock(CalendarWidget, {
                  key: contextGeneration.value,
                  context: calendarContext.value,
                  request: _unref(calendarStore).requestCalendar
                }, null, 8, ["context", "request"]))
        ], 64))
  ]))
}
}

})